/**
 * Trailer context — persists the shopper's identified trailer (a successful
 * reg-nr lookup) across sessions so the whole site can answer "does this fit
 * MY trailer?" without asking again.
 *
 * window.ValerydTrailer — one localStorage key holding the full normalised
 * EasySearch response (trailer spec, brake-ids, fitting article numbers), plus
 * two things the shopper's browsing needs after the lookup: an article-number
 * → collections index (`saveCategories` / `inCollection`, written once by
 * assets/easysearch.js while it has all the cards in hand) and whether the
 * filter is on (`filtering` / `setFiltering`, on from the moment a trailer is
 * identified, off only if the shopper says so). Mutations broadcast a
 * 'trailer:changed' DOM event so every consumer (<trailer-chip>, <trailer-fit>,
 * <trailer-filter>, <related-products>) re-renders live.
 *
 * The record is this module's alone: nothing else touches the storage key. The
 * index is what lets a category page state how many of the trailer's parts are
 * in it without a request — see assets/trailer-filter.js.
 *
 * <trailer-chip> — the saved reg-nr rendered into the header search shell
 * ([data-vehicle-slot]): a plate-style link to the parts page + a clear
 * button. Refreshes the stored parts data silently when it has gone stale.
 *
 * <trailer-fit> — a one-line "fits your trailer ABC123" confirmation that
 * unhides when one of the current product's SKUs is among the saved
 * trailer's fitting article numbers.
 *
 * The reg-nr lives only in the shopper's own browser — it is never sent to
 * analytics or third parties. Progressive enhancement throughout: without
 * JS none of this renders and the site works as before.
 */
(function () {
  var KEY = 'valeryd:trailer:v1';
  var EVENT = 'trailer:changed';
  var MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // refresh saved parts weekly

  var store = {
    read: function () {
      try {
        var raw = localStorage.getItem(KEY);
        var saved = raw ? JSON.parse(raw) : null;
        return saved && saved.regnr && saved.data ? saved : null;
      } catch (error) {
        return null;
      }
    },
    save: function (regnr, country, data) {
      var previous = store.read();
      var same = previous && previous.regnr === regnr;
      var record = {
        savedAt: Date.now(),
        regnr: regnr,
        country: country || '',
        data: data,
        // The weekly refresh re-saves the SAME trailer, and the category index it built is
        // keyed by article number, so it survives — a SKU that has since left the catalogue
        // simply stops matching. A DIFFERENT reg-nr is a different trailer: its index and its
        // filter state would both be lies, so they go.
        categories: same && previous.categories ? previous.categories : null,
        // Filtering is on from the moment a trailer is identified: the shopper asked for parts
        // that fit, and the answer should not need a second click on every page after.
        filter: same ? previous.filter !== false : true,
      };
      try {
        localStorage.setItem(KEY, JSON.stringify(record));
      } catch (error) {
        return; // storage full/blocked — feature simply stays off
      }
      document.dispatchEvent(new CustomEvent(EVENT));
    },
    // Which collections each fitting part belongs to: { '4010002A': ['axles-wheel-brakes', …] }.
    // Written once, when a lookup has already fetched every card anyway, so a category page can
    // answer "how many of this trailer's parts are in here" from the shopper's own browser
    // without a single request.
    saveCategories: function (map) {
      var saved = store.read();
      if (!saved || !map || !Object.keys(map).length) return;
      saved.categories = map;
      try {
        localStorage.setItem(KEY, JSON.stringify(saved));
      } catch (error) {
        return;
      }
      document.dispatchEvent(new CustomEvent(EVENT));
    },
    // Fitting article numbers in one collection, in the BACKEND's order — the same order the
    // fitment page shows, with in-stock articles floated to the front.
    //
    // Not the index's own order: an object's integer-like keys come out ascending and before
    // every other key, so `Object.keys` on an index of article numbers silently reorders it
    // into "numeric SKUs first, sorted" — a rule nobody chose. The response is the only place
    // the real order lives.
    inCollection: function (saved, handle) {
      var map = (saved && saved.categories) || null;
      if (!map || !handle) return [];
      var ordered = store.skus(saved);
      if (!ordered.length) ordered = Object.keys(map);
      return ordered.filter(function (sku) {
        return (map[sku] || []).indexOf(handle) !== -1;
      });
    },
    filtering: function (saved) {
      return Boolean(saved && saved.filter !== false && saved.categories);
    },
    setFiltering: function (on) {
      var saved = store.read();
      if (!saved) return;
      saved.filter = Boolean(on);
      try {
        localStorage.setItem(KEY, JSON.stringify(saved));
      } catch (error) {
        return;
      }
      document.dispatchEvent(new CustomEvent(EVENT));
    },
    clear: function () {
      try {
        localStorage.removeItem(KEY);
      } catch (error) {
        /* nothing to clear */
      }
      document.dispatchEvent(new CustomEvent(EVENT));
    },
    isStale: function (saved) {
      return !saved.savedAt || Date.now() - saved.savedAt > MAX_AGE_MS;
    },
    // Every fitting article number (== Shopify SKU) of the saved trailer,
    // uppercased for comparison. The response shape is ValerydFitment's to know.
    skus: function (saved) {
      return window.ValerydFitment ? window.ValerydFitment.skus(saved && saved.data) : [];
    },
  };
  window.ValerydTrailer = store;

  if (!customElements.get('trailer-chip')) {
    customElements.define(
      'trailer-chip',
      class TrailerChip extends HTMLElement {
        connectedCallback() {
          this.onChange = () => this.render();
          document.addEventListener(EVENT, this.onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT, this.onChange);
        }

        render() {
          var saved = store.read();
          if (!saved) {
            this.hidden = true;
            this.replaceChildren();
            return;
          }

          var link = document.createElement('a');
          link.className = 'trailer-chip__link';
          link.href = (this.dataset.searchUrl || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search') + '?q=' + encodeURIComponent(saved.regnr);
          link.textContent = saved.regnr;
          var trailer = saved.data && saved.data.trailer;
          link.title = window.ValerydFitment ? window.ValerydFitment.describe(trailer) : '';
          link.setAttribute('aria-label', (this.dataset.label || 'Your trailer') + ' ' + saved.regnr);

          var clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'trailer-chip__clear';
          clear.textContent = '×';
          clear.setAttribute('aria-label', this.dataset.remove || 'Forget saved trailer');
          clear.addEventListener('click', function () {
            store.clear();
          });

          this.replaceChildren(link, clear);
          this.hidden = false;

          if (store.isStale(saved)) this.refresh(saved);
        }

        // Re-fetch the saved trailer's parts so the stored lists track catalogue
        // changes. At most one attempt per page view; only a positive match
        // overwrites (an outage or a de-registered reg-nr keeps the old copy).
        async refresh(saved) {
          if (this.refreshAttempted || !window.ValerydFitment) return;
          this.refreshAttempted = true;
          // The registry to ask — the trailer's own country when we recorded one,
          // otherwise the page default (ValerydFitment falls back to SE).
          var result = await window.ValerydFitment.lookup(saved.regnr, { country: saved.country || this.dataset.country || '' });
          if (!result.ok) return; // outage or de-registered → keep the stale copy
          var data = result.data;
          var current = store.read();
          var unchanged =
            current &&
            current.regnr === saved.regnr &&
            current.country === saved.country &&
            current.savedAt === saved.savedAt;
          if (unchanged && data && data.found !== false && data.trailer) {
            store.save(saved.regnr, saved.country, data);
          }
        }
      }
    );
  }

  if (!customElements.get('trailer-fit')) {
    customElements.define(
      'trailer-fit',
      class TrailerFit extends HTMLElement {
        connectedCallback() {
          this.onChange = () => this.render();
          document.addEventListener(EVENT, this.onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT, this.onChange);
        }

        render() {
          var saved = store.read();
          var mine = (this.dataset.skus || '')
            .split(',')
            .map(function (sku) {
              return sku.trim().toUpperCase();
            })
            .filter(Boolean);
          var fits =
            saved &&
            mine.length &&
            store.skus(saved).some(function (sku) {
              return mine.indexOf(sku) !== -1;
            });
          if (!fits) {
            this.hidden = true;
            this.replaceChildren();
            return;
          }
          this.textContent = (this.dataset.text || '%REGNR%').replace('%REGNR%', saved.regnr);
          this.hidden = false;
        }
      }
    );
  }
})();
